import React, { useState, useEffect } from 'react';
import { ScannedItemsList } from './ScannedItemsList';
import { useGetSessionScannedItemsRequest, useDeleteSessionRequest } from '../../../hooks/useApi';
import { DeleteConfirmationModal } from '../../Inventory/components/DeleteConfirmationModal';
import ClockIcon from '../../../components/Icons/ClockIcon';
import CalendarIcon from '../../../components/Icons/CalendarIcon';
import DocumentIcon from '../../../components/Icons/DocumentIcon';
import DownChevronIcon from '../../../components/Icons/DownChevronIcon';
import './SessionItem.css';

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 5H4.16667H17.5" stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66667 5V3.33333C6.66667 2.89131 6.84226 2.46738 7.15482 2.15482C7.46738 1.84226 7.89131 1.66667 8.33333 1.66667H11.6667C12.1087 1.66667 12.5326 1.84226 12.8452 2.15482C13.1577 2.46738 13.3333 2.89131 13.3333 3.33333V5M15.8333 5L15 16.6667C15 17.1087 14.8244 17.5326 14.5118 17.8452C14.1993 18.1577 13.7754 18.3333 13.3333 18.3333H6.66667C6.22464 18.3333 5.80072 18.1577 5.48816 17.8452C5.17559 17.5326 5 17.1087 5 16.6667L4.16667 5H15.8333Z" stroke="#FF3B30" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const formatDateTime = (isoString) =>
{
  if (!isoString) return '';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

export const SessionItem = ({session, sessionIndex, inventoryId, posId, isExpanded, onToggle, inventoryStatus, onDeleted}) =>
{
  const [scannedItems, setScannedItems] = useState([]);
  const [{loading, error}, getSessionScannedItems] = useGetSessionScannedItemsRequest(posId);
  const [{loading: deleteLoading}, deleteSession] = useDeleteSessionRequest(posId);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteClick = (e) =>
  {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () =>
  {
    const result = await deleteSession({
      bodyParams: { inventoryId, sessionId: session.sessionId }
    });
    if (!result?.isAxiosError)
    {
      setShowDeleteConfirm(false);
      onDeleted?.();
    }
  };

  useEffect(() =>
  {
    if (isExpanded && scannedItems.length === 0)
    {
      // Fetch scanned items when session is expanded
      getSessionScannedItems({
        bodyParams: {
          inventoryId: inventoryId,
          sessionId: session.sessionId
        }
      }).then((response) =>
      {
        if (response?.data)
        {
          const transformedItems = response.data.map(item => ({
            ...item,
            style: (item.modelCode && item.fabricCode) ? `${item.modelCode}${item.fabricCode}` : null
          }));
          setScannedItems(transformedItems);
        }
      }).catch((err) =>
      {
        console.error('Error fetching scanned items:', err);
      });
    }
  }, [isExpanded, inventoryId, session.sessionId, posId, scannedItems.length]);

  const sessionName = session?.areaName || `Session ${sessionIndex}`;
  const displayTime = session.updatedOn ? formatDateTime(session.updatedOn) : '';

  return (
    <>
    <DeleteConfirmationModal
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDeleteConfirm}
      title="Delete Session"
      inventoryName={sessionName}
      isDeleting={deleteLoading}
    />
    <div className="session-item">
      <div className="session-header" onClick={onToggle}>
        <div className="session-header-left">
          <div className="session-title-wrapper">
            <ClockIcon/>
            <h3 className="session-title">{sessionName}</h3>
          </div>
          <div className="session-status-badge">
            <span className="session-status-text">{session.status}</span>
          </div>
          <div className="session-details">
            <div className="session-detail-row">
              <DocumentIcon width={18} height={18} color={'#717182'}/>
              <span className="session-detail-text">
            {session.itemsScanned} items Scanned
          </span>
            </div>
            <div className="session-detail-row">
              <CalendarIcon width={18} height={18} color={'#717182'}/>
              <span className="session-detail-text">{displayTime}</span>
            </div>
          </div>
        </div>
        <span className="session-created-text">
          Created By: {session.createdBy}
        </span>
        {inventoryStatus !== 'COMPLETED' && (
          <button
            className="session-delete-btn"
            onClick={handleDeleteClick}
            disabled={deleteLoading}
            title="Delete session"
          >
            <DeleteIcon/>
          </button>
        )}
        <div className={`session-chevron ${isExpanded ? 'expanded' : ''}`}>
          <DownChevronIcon/>
        </div>
      </div>

      {isExpanded && (
        <div className="session-scanned-items">
          {loading ? (
            <div className="session-items-loading">Loading items...</div>
          ) : error ? (
            <div className="session-items-error">Error loading items</div>
          ) : (
            <ScannedItemsList scannedItems={scannedItems}/>
          )}
        </div>
      )}
    </div>
    </>
  );
};
